import { inject, Injectable } from '@angular/core';
import { IVerknuepfungen } from '@mwe/models';
import { SessionStorage, SessionStorageService } from '@mwe/utils';
import { Observable } from 'rxjs';
import { productStore } from './product.store';

const associationSuggestionsKey = 'product_associationSuggestions';
const loggedMissingAssociationsKey = 'product_loggedMissingAssociations';
const loggedNewAssociationsKey = 'product_loggedNewAssociations';
const loggedAssociationSuggestionsKey = 'product_loggedAssociationSuggestions';
const associationPopupClosedKey = 'product_associationPopupClosed';

@Injectable({
  providedIn: 'root',
})
export class ProductStateService {
  @SessionStorage(loggedAssociationSuggestionsKey)
  _loggedAssociationSuggestions: string[];
  @SessionStorage(loggedMissingAssociationsKey)
  _loggedMissingAssociations: string[];
  @SessionStorage(loggedNewAssociationsKey)
  _loggedNewAssociations: string[];

  private storage = inject(SessionStorageService);
  private readonly productStore = inject(productStore);

  @SessionStorage(associationSuggestionsKey)
  _associationSuggestions: IVerknuepfungen[];

  get associationSuggestions(): IVerknuepfungen[] {
    return this._associationSuggestions || undefined;
  }

  set associationSuggestions(associationSuggestions: IVerknuepfungen[]) {
    if (associationSuggestions) {
      this._associationSuggestions = associationSuggestions;
    } else {
      this.storage.clear(associationSuggestionsKey);
    }
  }

  @SessionStorage(associationPopupClosedKey)
  _associationPopupClosed: boolean;

  get associationPopupClosed(): boolean {
    return this._associationPopupClosed;
  }

  set associationPopupClosed(associationPopupClosed: boolean) {
    this._associationPopupClosed = associationPopupClosed;
  }

  clear(): void {
    this.clearProducts();
    this.storage.clear(associationPopupClosedKey);
  }

  clearProducts(): void {
    this.productStore.setAssociations(undefined);
    this.productStore.setAssociationsOrig(undefined);
    this.storage.clear(associationSuggestionsKey);
  }

  clearRecentOrders(): void {
    this.productStore.setRecentOrders(undefined);
  }

  observeAssociationSuggestions(): Observable<IVerknuepfungen[]> {
    return this.storage.observe(associationSuggestionsKey);
  }

  hasLoggedAssociationSuggestion(association: IVerknuepfungen): boolean {
    return this._loggedAssociationSuggestions?.includes(association.geschaeftsPartnerId);
  }

  addLoggedAssociationSuggestion(association: IVerknuepfungen): void {
    if (!this._loggedAssociationSuggestions) {
      this._loggedAssociationSuggestions = [];
    }
    this._loggedAssociationSuggestions = [...this._loggedAssociationSuggestions, association.geschaeftsPartnerId];
  }

  hasLoggedMissingAssociation(association: IVerknuepfungen): boolean {
    return this._loggedMissingAssociations?.includes(association.geschaeftsPartnerId);
  }

  addLoggedMissingAssociation(association: IVerknuepfungen): void {
    if (!this._loggedMissingAssociations) {
      this._loggedMissingAssociations = [];
    }
    this._loggedMissingAssociations = [...this._loggedMissingAssociations, association.geschaeftsPartnerId];
  }

  hasLoggedNewAssociation(association: IVerknuepfungen): boolean {
    return this._loggedNewAssociations?.includes(association.geschaeftsPartnerId);
  }

  addLoggedNewAssociation(association: IVerknuepfungen): void {
    if (!this._loggedNewAssociations) {
      this._loggedNewAssociations = [];
    }
    this._loggedNewAssociations = [...this._loggedNewAssociations, association.geschaeftsPartnerId];
  }
}
